import { APIPaths } from '@bcm/apis';
import { StatusCodes } from 'http-status-codes';

import env, { REST_API_GATEWAYS } from '../../constants/env';
import { CONFERENCE_ROOM_USER, MESSAGE, NOTIFICATION, WS } from './utils/actionCases';
import wse from './utils/websocketEventEmitter';
import { actionDefaults } from './utils/actionUtils';
import { params } from '../reducers/conferenceRoomUserReducers';
import { MESSAGE_TYPES } from '../../constants/UIConstants';
import axios from 'axios';
import { DUPLEX_SERVICES } from '@bcm/constants';

const POLLING_TTL = 15 * 1000;

const initWsFunctions = (ws, dispatch) => {
	ws.onopen = async () => {
		wse.setDispatch(dispatch);
		await dispatch({ type: WS.CONNECT });
	};
	ws.onrefresh = async () => await dispatch({ type: WS.TIMEOUT });
	ws.onclose = async () => wse.closeWs();
	ws.onerror = (e) => console.log('Websocket error', e);
	ws.onmessage = async (e) => {
		// Process message data
		const processedData = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
		const processedBodies = (typeof processedData === 'string') ? [JSON.parse(processedData)]
			: Array.isArray(processedData) ? processedData.map(i => typeof i === 'string' ? JSON.parse(i) : i)
				: [ processedData ];
		for(const processedBody of processedBodies) {
			const req = typeof processedBody.body === 'string' ? JSON.parse(processedBody.body) : processedBody;
			// Check if reqId originated from this user
			if(wse.isSubscriber(req.reqId)) {
				const data = {
					data: req.body,
					status: req.statusCode,
				};
				delete data.statusCode;
				wse.publish(req.reqId, data);
			}
			else {
				if(req.action === 'notificationCreate') {
					await dispatch({
						type: NOTIFICATION.POST,
						notification: req.body,
					});
				}
				if(req.action === 'messageCreate') {
					await dispatch({
						type: MESSAGE.POST,
						message: req.body,
					});
					if (req.messageType === MESSAGE_TYPES.conferenceRoomLeave.key) {
						const res = {
							status: StatusCodes.OK,
							data: {
								identifier: {
									pk: req.authorString,
									sk: req.authorString
								},
							},
						};
						await dispatch(actionDefaults.deleteItem(CONFERENCE_ROOM_USER, res, params));
					}
					else if(req.messageType === MESSAGE_TYPES.conferenceRoomCreate.key
				|| req.messageType === MESSAGE_TYPES.conferenceRoomJoin.key) {
						const res = {
							status: StatusCodes.OK,
							data: {
								[params.itemName]: {
									identifier: {
										pk: req.authorString,
										sk: req.authorString
									},
									channelId: req.channelId,
									conferenceRoomId: req.text
								}
							},
						};
						await dispatch(actionDefaults.postItem(CONFERENCE_ROOM_USER, res, params));
					}
					else if(req.messageType === MESSAGE_TYPES.conferenceRoomEnd.key) {
						await dispatch({type: CONFERENCE_ROOM_USER.CLEAR_PAGES});
					}
				}
				// Websocket Actions
				const websocketActions = [ APIPaths.webRtcRoomUserJoin.WSRoute, APIPaths.webRtcRoomUserLeave.WSRoute, APIPaths.webRtcRoomUserMessage.WSRoute ];
				if(websocketActions.includes(req.action)) wse.publish('webrtc', req);
			}
		}
	};
	return ws;
};

class LongPollingConnection {
	constructor(idToken, STAGE) {
		this.idToken = idToken;
		this.isIdTokenExpired = false;
		this.failureCount = 0;
		this.STAGE = STAGE;
		this.pollingPath = `${REST_API_GATEWAYS[STAGE]}/api/v1/polling`;
	}
	onopen = async () => {};
	onclose = async () => {};
	onerror = () => {};
	onmessage = () => {};
	updateIdToken = (newIdToken) => {
		this.idToken = newIdToken;
		this.isIdTokenExpired = false;
		this.poll();
	};
	updateStage = (STAGE) => {
		this.STAGE = STAGE;
		this.pollingPath = `${REST_API_GATEWAYS[STAGE]}/api/v1/polling`;
		this.poll();
	};
	poll = async () => {
		if(this.isIdTokenExpired) return;
		try {
			const res = await axios.get(this.pollingPath, {
				timeout: POLLING_TTL,
				headers: {
					Authorization: `Bearer ${this.idToken}`
				}
			});
			if(res.status === StatusCodes.OK) this.onmessage(res);
			else if(res.status === StatusCodes.FORBIDDEN) {
				this.isIdTokenExpired = true;
				await this.onclose();
			}
			this.failureCount = 0;
			this.poll();
		}
		catch (err) {
			if(this.failureCount >= 3) {
				await this.onclose();
			}
			else if(err && err.response && err.response.status === StatusCodes.FORBIDDEN) {
				this.isIdTokenExpired = true;
				await this.onrefresh();
			}
			else if(err.code === 'ECONNABORTED') {
				this.failureCount = 0;
				this.poll();
			}
			else if(this.failureCount < 3){
				this.failureCount = this.failureCount + 1;
				this.poll();
			}
		}
	};
}

export default {

	initWsFunctions: initWsFunctions,

	onConnect: (idToken) => async (dispatch) => {
		let ws;
		if(env.DUPLEX_SERVICE === DUPLEX_SERVICES.polling) ws = new LongPollingConnection(idToken, env.STAGE);
		else ws = new WebSocket(`${env.WEBSOCKET_API_GATEWAY}?authorization=${idToken}`);
		ws = initWsFunctions(ws, dispatch);
		if(env.DUPLEX_SERVICE === DUPLEX_SERVICES.polling) {
			await ws.onopen();
			// Delay initiall polling request to prevent automatic failure to close
			setTimeout(() => ws.poll(), 3000);
		}
		wse.setWs(ws);
	},
};